import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { logAnalyticsEvent } from '@vestico/utils';

import { ProductWidget } from '@vestico/model';

import { ConfigContext } from '../WidgetEventsManager';

export interface RealTimePriceProps {
  product?: ProductWidget | null;
}

interface ProductResult {
  compare_at_price_min: number;
  compare_at_price: number | null;
  price_min: number;
  price_varies: boolean;
}

export interface RealTimePrice {
  min: number;
  originalMin: number | null;
  varies: boolean;
}

const fetchProductPrice = async (product: ProductWidget) => {
  // ENABLE FOR LOCAL TESTING
  // const realTimePriceX: ProductResult = {
  //   price_min: 20000,
  //   compare_at_price_min: 49950,
  //   compare_at_price: 49950,
  //   price_varies: false,
  // };
  // return realTimePriceX;

  const productHandle = product.url.split('/').pop();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = await fetch(`${(window as any).Shopify.routes.root}products/${productHandle}.js`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!result || result.status !== 200) {
    return undefined;
  }

  const resultJSON: ProductResult = await result.json();
  return resultJSON;
};

export const useRealTimePrice = ({ product }: RealTimePriceProps) => {
  const { type, currencyCode } = useContext(ConfigContext);
  const enabled = useMemo(() => !!product && type === 'shopify' && !!currencyCode && currencyCode.length > 0, [currencyCode, product, type]);

  const [realTimePrice, setRealTimePrice] = useState<RealTimePrice | undefined>(undefined);

  const getRealTimePrice = useCallback(async () => {
    if (!enabled || !product) {
      return undefined;
    }

    try {
      const resultJSON = await fetchProductPrice(product);
      if (!resultJSON) {
        return undefined;
      }

      const { compare_at_price: originalPrice, compare_at_price_min: originalMinPrice, price_min: min, price_varies: varies } = resultJSON;

      const minDecimal = min / 100;

      const realTimePrice: RealTimePrice = {
        min: minDecimal,
        originalMin: originalPrice && originalMinPrice !== min ? originalMinPrice / 100 : null,
        varies,
      };

      return realTimePrice;
    } catch (error) {
      console.error(error);
      logAnalyticsEvent({ category: 'exception', event: 'realTimePrice', name: error.message });
    }
    return undefined;
  }, [enabled, product]);

  useEffect(() => {
    getRealTimePrice().then((p) => setRealTimePrice(p));
  }, [getRealTimePrice]);

  return {
    enabled,
    realTimePrice,
  };
};
